import "./EmailVerification.css";

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useUnleashContext } from "@unleash/proxy-client-react";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { useSignIn } from "react-auth-kit";
import logo from "../abe_logo.png";
import { PAGES } from "../constants";

function EmailVerification() {
  const navigate = useNavigate();
  const { state, search } = useLocation();
  const signIn = useSignIn();
  const updateContext = useUnleashContext();

  const queryParams = new URLSearchParams(search);
  const defaultVerificationCode = queryParams.get("verification_code");
  const defaultEmail = state?.email || queryParams.get("email");

  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const [verificationError, setVerificationError] = useState("");

  const emailRef = useRef(null);
  const verificationCodeRef = useRef(null);

  const setInputs = () => {
    setEmail(emailRef.current.value);
    setVerificationCode(verificationCodeRef.current.value);
  }

  useEffect(() => {
    setInputs();
    if (defaultEmail && defaultVerificationCode) {
      handleVerification(null, defaultEmail, defaultVerificationCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerification = (event, email, verificationCode) => {
    if (event) {
      event.preventDefault();
    }

    const verificationUrl = `${process.env.REACT_APP_BASE_URL}/user/verify_email`;
    const verificationData = {
      email: email,
      verification_code: verificationCode,
    };
    const verificationConfig = {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    };

    axios.post(verificationUrl, verificationData, verificationConfig)
      .then(response => {
        const jwtDecoded = jwt_decode(response.data.access_token);
        const expiryEpoch = jwtDecoded.exp; // Expiry epoch in seconds
        const currentEpoch = (new Date()).getTime() / 1000; // Current epoch in seconds
        const expiresIn = Math.ceil((expiryEpoch - currentEpoch) / 60) // Amount of minutes until expiry
        if (signIn(
          {
            token: response.data.access_token,
            expiresIn: Math.max(expiresIn, 0), // Ensure expiresIn is not negative
            tokenType: response.data.token_type,
            authState: { email: jwtDecoded.sub }
          }
        )) {
          updateContext({ userId: jwtDecoded.sub });
          navigate(PAGES.HOME);
        }
      })
      .catch(error => {
        if (error.response) {
          setVerificationError(error.response.data.detail);
        } else {
          setVerificationError("Unable to connect to server");
        }
      });
  }

  const handleResendVerificationEmail = () => {
    const searchParams = new URLSearchParams({
      email: email
    });
    const verificationUrl = `${process.env.REACT_APP_BASE_URL}/user/resend_verification_email?${searchParams.toString()}`;
    const verificationData = {};
    const verificationConfig = {
      headers: { "accept": "application/json" },
    };

    axios.post(verificationUrl, verificationData, verificationConfig)
      .then(response => {
        setVerificationError("Verification email has been sent");
      })
      .catch(error => {
        if (error.response) {
          setVerificationError(error.response.data.detail);
        } else {
          setVerificationError("Unable to connect to server");
        }
      });
  }

  return (
    <div className="login-container" data-testid="email-verification-page-div">
      <img src={logo} className="logo" alt="logo" />
      <p className="verification-info-text">Please verify your email address to continue</p>

      <form className="verification-form" onSubmit={(event) => handleVerification(event, email, verificationCode)}>
        <input className="auth-input" type="text" placeholder="Email" ref={emailRef} onChange={setInputs} defaultValue={defaultEmail} />

        <input className="auth-input" type="text" placeholder="Verification Code" ref={verificationCodeRef} onChange={setInputs} defaultValue={defaultVerificationCode} />

        <div className="auth-center-contents">
          {verificationError !== "" && <p className="error-message">{verificationError}</p>}
          <input type="submit" className="auth-button" value="Verify" />
        </div>
      </form>

      <button className="authentication-form-hyperlink" onClick={handleResendVerificationEmail}>Resend verification email</button>
    </div>
  );
}

export default EmailVerification;